import { reportText } from '../reportSiteIdentity';
import { admitReportSources, projectReportSourceEvidence } from './reportEvidenceSourceAdmission';
import {
  isRecordStructuredTopographySurveySupport,
  NEGATED_OR_MISSING_TOPOGRAPHY_RE,
} from '../reportRendering';
import { buildCampusResourceAdvisoryDocumentBlocks, readCampusResourceAdvisory } from '../findings/campusResourceAdvisory';
import { buildRegulatoryFeasibilityDocumentBlocks, readRegulatoryFeasibilityScreen } from '../regulatoryFeasibility/frozen';
import { buildSiteResourceDocumentBlocks } from '../reportRendering/siteResourceReportProjection';
import { frozenProcurementOverview, materialScopeDisclosure } from '../supplyChainMaterialScope';
import { dataCenterFinancialSectionText, publicDataCenterFinancialStamp } from '../reportRendering/dataCenterFinancialProjection';
import { hasValidatedDeterministicScreening } from './deterministicScreeningValidation';
import { reportDemonstrationDisclosure } from '../demonstration/disclosure';
import { hasCoverageOnlySentimentOmission, hasStanceOnlySentimentOmission } from '../sentiment/publicSentimentScreening';
import {
  getSectionLabel,
  getSectionContractsForType,
  getSectionsForType,
  migrateSectionIdForType,
  RETIRED_SECTION_IDS,
  toReportSectionContract,
  type ReportSectionContract,
} from '../../config/reportSections';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import type { SiteRagReport } from '../../types/siteRag';
import {
  presentationSnapshotV1Schema,
  reportDocumentModelV1Schema,
  type EvidenceValue,
  type JsonValue,
  type PresentationSnapshotV1,
  type ReportDocumentBlockV1,
  type ReportDocumentModelV1,
} from '../../types/reportContractsV2';
import { canonicalJson, hashCanonicalJson } from './reportContractHash';
import { hashReportDocumentSemanticContent } from './reportDocumentIdentity';
import { customBriefSourceFrame, CUSTOM_BRIEF_SOURCE_FRAME_FIELD } from './customBriefSourceCurrency';
import { resolveReportBlindSpots } from '../resolveReportBlindSpots';
import { REPORT_PAGE_MARGIN_MM } from '../reportLayoutTokens';
import { validateGenerationLayoutExhibitSnapshot } from '../generationImpact/generationLayoutExhibit';
import { generationLayoutExhibitHasContent } from '../tocEntries';
import type {
  GenerationImpactSnapshot,
  GenerationProjectDutyExhibitBasis,
} from '../../types/generationImpact';
import { projectPublicGenerationProjectDutyExhibitBasis } from '../generationImpact/generationProjectDuty';
import { withDerivedTableReferences } from '../report/derivedTableReferences';
import { buildWorkloadPowerFitContent } from '../report/workloadPowerFitContent';
import { readGridLedStandbyPlanningReportProjection } from '../generationImpact/gridLedStandbyPlanningReportContent';
import { deriveIntentForPlanningReport } from '../generationImpact/selectedAssessmentIntent';
import {
  getByPath,
  SECTION_OBJECT_DATA_KEY,
  SECTION_SCALAR_DATA_KEY,
} from '../report/sectionDataKeys';
import { SECTION_FIELD_PATHS } from '../sectionFieldPaths';
import { withoutPublicSentimentMetaV2 } from '../sentiment/publicSentimentMetaV2Contract';
import {
  WITHHELD_SECTION_OMISSION_DETAIL,
  materializeWithheldNarrative,
  withheldNarrativeLeaves,
} from '../report/withheldNarrative';
import {
  bindValidatedScrubContext,
  deriveScrubScenarioContext,
} from '../reportRendering/scrubInternalSentinels';

// The schema inference names are deliberately re-exported below. Keeping the
// builder's internal aliases local avoids coupling callers to implementation
// details while retaining exact schema-derived output types.
type DocumentSection = ReportDocumentModelV1['sections'][number];
type DocumentOmission = ReportDocumentModelV1['omissions'][number];

const NON_CONTENT_SECTION_IDS = new Set(['toc']);
const OPTIONAL_SECTION_IDS = new Set([
  'appendix',
  'blindSpots',
  'radarChart',
  'lcoeChart',
  'pathway_comparison',
  'generationLayoutExhibit',
  'sentimentResearchDetail',
  'sentimentAppendix',
]);

const ANALYSIS_CONTAINERS = [
  'powerAnalysis',
  'waterAnalysis',
  'testFitAnalysis',
  'interconnectionAnalysis',
  'supplyChainAnalysis',
] as const;

const SECTION_ALIASES: Readonly<Record<string, readonly string[]>> = {
  executive: ['executive', 'executiveSummary'],
  executiveSummary: ['executiveSummary', 'summary'],
  projectIntentOverview: ['projectIntentOverview', 'projectIntentAtGen'],
  projectContext: ['customBriefProjectContext'],
  btmOverview: ['btmDetailedAnalysis', 'btmOptions'],
  workloadPowerFit: ['workloadPowerFit', 'firmCapacityVerdict'],
  // STAGE 5.1 — `'gridStudy'` REMOVED. `SECTION_ALIASES` is the flat
  // leaf-name map the comment further down warns about, and it is consulted
  // BEFORE the type-scoped `SECTION_FIELD_PATHS`. While `gridCapacity` was
  // power-availability-only the alias was inert (POWER_AVAILABILITY_SCHEMA has
  // no `gridStudy` field). BTM Feasibility now carries BOTH sections, so the
  // alias would hand `gridStudy`'s content to `gridCapacity` whenever
  // `powerAnalysis.substationAssessment` is legitimately absent — reproducing
  // the 2026-06-10 similarity-1.0 collision this very function documents.
  gridCapacity: ['gridCapacity'],
  fiberRings: ['fiberAvailability'],
  latencyModel: ['latencyModeling'],
  bandwidthCapacity: ['bandwidthCapacity', 'edgeDeployment'],
  connectivityRiskMatrix: ['risks'],
  criticalPathProcurement: ['criticalPathAssessment'],
  supplyChainRisks: ['supplyChainRiskSynthesis'],
  quoteComparisons: ['quoteComparisonInsights'],
  actionPlan: ['developmentPathway', 'recommendations'],
  financialActionPlan: ['recommendations'],
  riskAssessment: ['developmentRisks', 'risks'],
  powerRiskMatrix: ['risks'],
  waterRiskMatrix: ['risks'],
  supplyChainRiskMatrix: ['risks'],
  environmentalRiskMatrix: ['risks'],
  riskMatrix: ['risks'],
  radarChart: ['categories', 'contextualKeyFindings'],
  lcoeChart: ['lcoeDetailedAnalysis', 'pathwayLcoeAtGen'],
  conclusion: ['conclusion'],
  crossReportSynthesis: ['crossReportSynthesis'],
  criticalRisks: ['executiveSummaryCriticalRisks'],
  sourceReports: ['executiveSummarySourcesUsed'],
  btmViability: ['btmViabilityAssessment'],
  decisionBand: ['financialAnalysis'],
  financialSummary: ['financialAnalysis'],
  capexOpexBreakdown: ['financialAnalysis'],
  cashflowStream: ['financialAnalysis'],
  monteCarloBands: ['financialAnalysis'],
  riskAdjustedWacc: ['financialAnalysis'],
  sensitivity: ['financialAnalysis'],
  taxAndCommercial: ['financialAnalysis'],
  execDecisionBand: ['executiveDecisionRollupAtGen'],
  pathToGo: ['pathToGoSnapshotAtGen'],
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

function toJsonValue(value: unknown): JsonValue | undefined {
  if (value === undefined) return undefined;
  return JSON.parse(canonicalJson(value)) as JsonValue;
}

function hasContent(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(hasContent);
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).some(hasContent);
  return true;
}

function uniqueStrings(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => value.trim().length > 0))];
}

function publicSafeSourceIds(sourceIds: readonly string[]): string[] {
  return sourceIds.filter((sourceId) => /^(?:ref|provider|provenance|citation):/i.test(sourceId));
}

function reportRecord(report: SiteRagReport): Record<string, unknown> {
  return report as unknown as Record<string, unknown>;
}

function canonicalGenerationImpactSnapshot(value: unknown): GenerationImpactSnapshot | null {
  if (!isPlainObject(value) || !('noise' in value) || !isPlainObject(value.scenario)) return null;
  const scenario = value.scenario;
  if (
    typeof scenario.id !== 'string'
    || typeof scenario.inputHash !== 'string'
    || typeof scenario.resultHash !== 'string'
    || !isPlainObject(scenario.result)
    || typeof scenario.result.modelVersion !== 'string'
  ) return null;
  return value as unknown as GenerationImpactSnapshot;
}

function findSectionValues(
  report: SiteRagReport,
  sectionId: string,
  reportType?: ReportTypeId,
): unknown[] {
  const planningProjection = readGridLedStandbyPlanningReportProjection(report);
  const intentForDisplay = planningProjection
    ? deriveIntentForPlanningReport(report, report.projectIntentAtGen)
    : report.projectIntentAtGen;
  // Every alias and typed field uses the same active view. The original
  // declaration remains frozen on the report and in its private history.
  const raw = intentForDisplay === report.projectIntentAtGen
    ? reportRecord(report)
    : { ...reportRecord(report), projectIntentAtGen: intentForDisplay };
  if (sectionId === 'gridLedStandbyPlanning') {
    return planningProjection ? [planningProjection] : [];
  }
  if (reportType === 'supply-chain-risk' && sectionId === 'procurementOverview' && materialScopeDisclosure(report.supplyChainPlanAtGen)) {
    return [frozenProcurementOverview(report)];
  }
  if (sectionId === 'generationLayoutExhibit') {
    const snapshot = raw.generationLayoutExhibitAtGen;
    if (!isPlainObject(snapshot)) return [];
    const alignedImpact = canonicalGenerationImpactSnapshot(raw.generationImpactAtGen);
    if (!alignedImpact || !validateGenerationLayoutExhibitSnapshot(snapshot, alignedImpact).valid) return [];
    const scenario = isPlainObject(snapshot.scenario) ? snapshot.scenario : {};
    const engineering = isPlainObject(snapshot.engineering) ? snapshot.engineering : {};
    const fleet = isPlainObject(engineering.fleet) ? engineering.fleet : {};
    const capacity = isPlainObject(engineering.capacity) ? engineering.capacity : {};
    const gas = isPlainObject(engineering.gas) ? engineering.gas : {};
    const water = isPlainObject(engineering.water) ? engineering.water : {};
    const emissions = isPlainObject(engineering.emissions) ? engineering.emissions : {};
    const footprint = isPlainObject(engineering.footprint) ? engineering.footprint : {};
    const equipmentBasis = isPlainObject(engineering.equipmentBasis)
      ? engineering.equipmentBasis
      : null;
    const projectDutyBasis = isPlainObject(engineering.projectDutyBasis)
      ? projectPublicGenerationProjectDutyExhibitBasis(
          engineering.projectDutyBasis as unknown as GenerationProjectDutyExhibitBasis,
        )
      : null;
    const sizingStudy = isPlainObject(engineering.sizingStudy) ? engineering.sizingStudy : null;
    const nearestReceptor = isPlainObject(engineering.nearestReceptor) ? engineering.nearestReceptor : null;
    const lighting = isPlainObject(engineering.lighting) ? engineering.lighting : {};
    const provenance = isPlainObject(snapshot.provenance) ? snapshot.provenance : {};
    const frozenAuthority = isPlainObject(provenance.frozenAuthority)
      ? provenance.frozenAuthority
      : null;
    const fit = isPlainObject(snapshot.fit) ? snapshot.fit : {};
    const plan = isPlainObject(fit.plan) ? fit.plan : {};
    const requiredEnvelope = isPlainObject(plan.requiredEnvelope) ? plan.requiredEnvelope : {};
    const reservationSummary = isPlainObject(plan.reservationSummary)
      ? plan.reservationSummary
      : null;
    const overlays = isPlainObject(snapshot.overlays) ? snapshot.overlays : {};
    const cadSources = Array.isArray(overlays.cadSources)
      ? overlays.cadSources.filter(isPlainObject).map((source) => ({
          sourceFile: source.sourceFile,
          sourceSha256: source.sourceSha256,
          dwgVersion: source.dwgVersion,
          featureCount: source.featureCount,
          layerNames: Array.isArray(source.layerNames)
            ? source.layerNames.filter((name): name is string => typeof name === 'string')
            : [],
        }))
      : [];
    // Semantic projection is intentionally scalar/ledger-only. The frozen
    // coordinate arrays remain in the exhibit asset/snapshot and must never be
    // duplicated into a generic structured-data block (which would inflate the
    // document and create false office-projection obligations).
    return [{
      schemaVersion: snapshot.schemaVersion,
      modelVersion: snapshot.modelVersion,
      capturedAt: snapshot.capturedAt,
      contentHash: snapshot.contentHash,
      scenario: {
        id: scenario.id,
        name: scenario.name,
        inputHash: scenario.inputHash,
        resultHash: scenario.resultHash,
        hashVersion: scenario.hashVersion,
        modelVersion: scenario.modelVersion,
        updatedAt: scenario.updatedAt,
      },
      ...(frozenAuthority
        ? {
            authority: {
              contractVersion: frozenAuthority.contractVersion,
              scenarioId: frozenAuthority.scenarioId,
              scenarioUpdatedAt: frozenAuthority.scenarioUpdatedAt,
              inputHash: frozenAuthority.inputHash,
              resultHash: frozenAuthority.resultHash,
              modelVersion: frozenAuthority.modelVersion,
              promotedAt: frozenAuthority.promotedAt,
              layoutPublicationId: frozenAuthority.layoutPublicationId,
              layoutVersion: frozenAuthority.layoutVersion,
              layoutHash: frozenAuthority.layoutHash,
              layoutPublishedAt: frozenAuthority.layoutPublishedAt,
              boundaryHash: frozenAuthority.boundaryHash,
              boundaryAreaAcres: frozenAuthority.boundaryAreaAcres,
              ...(typeof frozenAuthority.sedId === 'string'
                ? { sedId: frozenAuthority.sedId }
                : {}),
              ...(typeof frozenAuthority.sedVersion === 'number'
                ? { sedVersion: frozenAuthority.sedVersion }
                : {}),
            },
          }
        : {}),
      engineering: {
        fleet: {
          technologyGroupCount: fleet.technologyGroupCount,
          generationBlockCount: fleet.generationBlockCount,
          isoGrossNameplateMW: fleet.isoGrossNameplateMW,
          siteGrossMW: fleet.siteGrossMW,
        },
        capacity: {
          normalNetMW: capacity.normalNetMW,
          largestContingencyGroupId: capacity.largestContingencyGroupId,
          largestContingencyMW: capacity.largestContingencyMW,
          nMinusOneNetMW: capacity.nMinusOneNetMW,
          criticalLoadMW: capacity.criticalLoadMW,
          reserveRequiredMW: capacity.reserveRequiredMW,
          nMinusOneMarginMW: capacity.nMinusOneMarginMW,
          nMinusOneReserveMarginMW: capacity.nMinusOneReserveMarginMW,
          nMinusOneVerdict: capacity.nMinusOneVerdict,
          nMinusOneReserveVerdict: capacity.nMinusOneReserveVerdict,
        },
        gas: {
          peakMmbtuPerHour: gas.peakMmbtuPerHour,
          peakDthPerDay: gas.peakDthPerDay,
          annualMmbtu: gas.annualMmbtu,
        },
        water: {
          generationWithdrawalGalPerDay: water.generationWithdrawalGalPerDay,
          generationConsumptionGalPerDay: water.generationConsumptionGalPerDay,
          ccsWithdrawalGalPerDay: water.ccsWithdrawalGalPerDay,
          ccsConsumptionGalPerDay: water.ccsConsumptionGalPerDay,
          dataCenterWithdrawalGalPerDay: water.dataCenterWithdrawalGalPerDay,
          dataCenterConsumptionGalPerDay: water.dataCenterConsumptionGalPerDay,
          totalWithdrawalGalPerDay: water.totalWithdrawalGalPerDay,
          totalConsumptionGalPerDay: water.totalConsumptionGalPerDay,
        },
        emissions: {
          uncontrolledCo2TonnesPerYear: emissions.uncontrolledCo2TonnesPerYear,
          capturedCo2TonnesPerYear: emissions.capturedCo2TonnesPerYear,
          residualCo2TonnesPerYear: emissions.residualCo2TonnesPerYear,
          noxTonnesPerYear: emissions.noxTonnesPerYear,
          coTonnesPerYear: emissions.coTonnesPerYear,
        },
        footprint: {
          plantAcres: footprint.plantAcres,
          coolingAcres: footprint.coolingAcres,
          switchyardAcres: footprint.switchyardAcres,
          ccsAcres: footprint.ccsAcres,
          compressionAcres: footprint.compressionAcres,
          totalAcres: footprint.totalAcres,
        },
        nearestReceptor: nearestReceptor && {
          receptorId: nearestReceptor.receptorId,
          receptorName: nearestReceptor.receptorName,
          distanceM: nearestReceptor.distanceM,
          predictedDbA: nearestReceptor.predictedDbA,
          thresholdDbA: nearestReceptor.thresholdDbA,
          marginDb: nearestReceptor.marginDb,
          verdict: nearestReceptor.verdict,
        },
        lighting: {
          installedPowerKW: lighting.installedPowerKW,
          installedLumens: lighting.installedLumens,
          planningFlags: Array.isArray(lighting.planningFlags)
            ? lighting.planningFlags.filter((flag): flag is string => typeof flag === 'string')
            : [],
        },
        gaps: Array.isArray(engineering.gaps)
          ? engineering.gaps.filter((gap): gap is string => typeof gap === 'string')
          : [],
        warnings: Array.isArray(engineering.warnings)
          ? engineering.warnings.filter((warning): warning is string => typeof warning === 'string')
          : [],
        noiseCaveats: Array.isArray(engineering.noiseCaveats)
          ? engineering.noiseCaveats.filter((caveat): caveat is string => typeof caveat === 'string')
          : [],
        equipmentBasis: equipmentBasis
          ? JSON.parse(canonicalJson(equipmentBasis)) as JsonValue
          : null,
        projectDutyBasis: projectDutyBasis
          ? JSON.parse(canonicalJson(projectDutyBasis)) as JsonValue
          : null,
        sizingStudy: sizingStudy
          ? JSON.parse(canonicalJson(sizingStudy)) as JsonValue
          : null,
      },
      fit: {
        ...(fit.fitModelVersion === 'generation-test-fit-v2'
          ? { fitModelVersion: fit.fitModelVersion }
          : {}),
        status: fit.status,
        containedAcres: fit.containedAcres,
        outsideAcres: fit.outsideAcres,
        containmentPct: fit.containmentPct,
        minimumParcelEdgeClearanceM: fit.minimumParcelEdgeClearanceM,
        parcelEdgeSetbackSatisfied: fit.parcelEdgeSetbackSatisfied,
        selectedParcelId: provenance.selectedParcelId,
        requiredEnvelopeWidthM: requiredEnvelope.widthM,
        requiredEnvelopeHeightM: requiredEnvelope.heightM,
        requiredEnvelopeAcres: requiredEnvelope.areaAcres,
        ...(reservationSummary
          ? {
              reservationSummary: {
                permanentEquipmentAcres: reservationSummary.permanentEquipmentAcres,
                routeReservationAcres: reservationSummary.routeReservationAcres,
                constructionLaydownAcres: reservationSummary.constructionLaydownAcres,
                totalReservedAcres: reservationSummary.totalReservedAcres,
              },
            }
          : {}),
      },
      overlays: {
        status: overlays.status,
        sourceLayerCount: provenance.sourceLayerCount,
        visibleLayerCount: provenance.visibleLayerCount,
        includedLayerCount: Array.isArray(overlays.includedLayerIds) ? overlays.includedLayerIds.length : 0,
        omittedLayerCount: Array.isArray(overlays.omittedLayerIds) ? overlays.omittedLayerIds.length : 0,
        invalidLayerCount: Array.isArray(overlays.invalidLayerIds) ? overlays.invalidLayerIds.length : 0,
        totalVertices: overlays.totalVertices,
        cadSources,
      },
      warnings: Array.isArray(snapshot.warnings)
        ? snapshot.warnings.filter((warning): warning is string => typeof warning === 'string')
        : [],
    }];
  }
  const keys = uniqueStrings([sectionId, ...(SECTION_ALIASES[sectionId] ?? [])]);
  const candidates: unknown[] = [];

  for (const key of keys) {
    if (hasContent(raw[key])) candidates.push(raw[key]);
  }

  for (const containerKey of ANALYSIS_CONTAINERS) {
    const container = raw[containerKey];
    if (!isPlainObject(container)) continue;
    for (const key of keys) {
      if (hasContent(container[key])) candidates.push(container[key]);
    }
  }

  // Shared id→data-key resolution. A section id frequently DIFFERS from the
  // report field carrying its data (`coolingByTech` ← `waterAnalysis.
  // coolingWaterRequirements`), and matching by id alone silently produced a
  // section with zero blocks — which the decision-analysis schema then rejects
  // as "a required section cannot be empty", killing the whole generation while
  // the DOCX/PPTX exports rendered the same content correctly.
  //
  // `SECTION_FIELD_PATHS` is the reviewed, per-report-type correspondence, and
  // being keyed BY TYPE it cannot collide the way a flat leaf-name map would:
  // water-resource's `totalWaterCost` and test-fit's `maxCapacity` once both
  // resolved `testFitAnalysis.recommendations` and rendered byte-identical
  // (the 2026-06-10 similarity-1.0 collision). Source from it rather than
  // maintaining a private copy here.
  const typedFieldPath = reportType ? SECTION_FIELD_PATHS[reportType]?.[sectionId] : undefined;
  for (const path of [
    ...(typedFieldPath ? [typedFieldPath] : []),
    ...(SECTION_SCALAR_DATA_KEY[sectionId] ?? []),
  ]) {
    // The canonical document (and so every export, the PDF appendix and the
    // public share projection) carries only the legacy sentiment meta keys.
    // The additive psr-meta-v2 projection holds verbatim source excerpts and
    // is rendered by the report view; a report without it is unchanged.
    const value = path === 'publicSentimentMeta' ? withoutPublicSentimentMetaV2(getByPath(raw, path)) : getByPath(raw, path);
    if (hasContent(value)) candidates.push(value);
  }

  // Object sections project ONLY the descriptor's named fields, never the whole
  // container — `groundwater` must not absorb the rest of `waterAnalysis`.
  const objectDescriptor = SECTION_OBJECT_DATA_KEY[sectionId];
  if (objectDescriptor) {
    const container = getByPath(raw, objectDescriptor.container);
    if (isPlainObject(container)) {
      const projected: Record<string, unknown> = {};
      for (const [dataKey] of objectDescriptor.fields) {
        if (hasContent(container[dataKey])) projected[dataKey] = container[dataKey];
      }
      if (hasContent(projected)) candidates.push(projected);
    }
  }

  if (sectionId === 'transportStudy') {
    if (hasContent(raw.connectivityStudy)) candidates.push(raw.connectivityStudy);
  } else if (sectionId === 'sitingNotes') {
    if (hasContent(raw.climateSevereWeather)) candidates.push(raw.climateSevereWeather);
  } else if (sectionId === 'appendix') {
    for (const key of ['appendix', 'crossValidationFindings', 'dataGaps', 'sedGaps']) {
      if (hasContent(raw[key])) candidates.push(raw[key]);
    }
  } else if (sectionId === 'blindSpots') {
    return resolveReportBlindSpots(report).map(({ gap, impact }) => ({
      ...gap,
      effectiveImpact: impact,
    }));
  } else if (sectionId === 'actionPlan' || sectionId === 'financialActionPlan') {
    const conclusion = raw.conclusion;
    if (isPlainObject(conclusion) && hasContent(conclusion.nextActions)) {
      candidates.push(conclusion.nextActions);
    }
  }

  if (report.financialAnalysis?.modelKind === 'data-center-capacity-fee') {
    if (sectionId === 'financialSummary') return [publicDataCenterFinancialStamp(report.financialAnalysis)];
    const text = dataCenterFinancialSectionText(report.financialAnalysis, sectionId);
    if (text) return [text];
  }

  // Financial sections project only the relevant deterministic sub-snapshot,
  // rather than repeating the entire finance object on every page.
  if (isPlainObject(raw.financialAnalysis)) {
    const finance = raw.financialAnalysis;
    const financialProjection: Partial<Record<string, unknown>> = {
      decisionBand: {
        band: finance.band,
        viabilityScore: finance.viabilityScore,
        bindingConstraints: finance.bindingConstraints,
        rationale: finance.rationale,
      },
      financialSummary: finance.headline,
      monteCarloBands: finance.monteCarlo,
      riskAdjustedWacc: isPlainObject(finance.headline)
        ? { waccPct: finance.headline.waccPct, hurdleRatePct: finance.headline.hurdleRatePct }
        : undefined,
    };
    const projection = financialProjection[sectionId];
    if (hasContent(projection)) return [projection];
  }

  // The reader and office exports build this reference section from the
  // frozen Intent. Absence of a firm-capacity verdict is expected during
  // screening and must not erase the workload section in the immutable copy.
  // Never use this fallback to replace a withheld narrative or metadata body.
  if (sectionId === 'workloadPowerFit' && candidates.length === 0
    && report.projectIntentAtGen && !report._metadataOnly
    && !withheldNarrativeLeaves(report).some(leaf => /^(?:workloadPowerFit|firmCapacityVerdict)(?:\.|\[|$)/.test(leaf.path))) {
    candidates.push(buildWorkloadPowerFitContent(report));
  }
  const seen = new Set<string>();
  return candidates.filter((candidate) => {
    const identity = canonicalJson(candidate);
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
}

function sourceIdsFromValue(value: unknown): string[] {
  if (!isPlainObject(value)) return [];
  const direct = value.sourceIds;
  const docs = value.contributingDocIds;
  return uniqueStrings([
    ...(Array.isArray(direct) ? direct.filter((item): item is string => typeof item === 'string') : []),
    ...(Array.isArray(docs) ? docs.filter((item): item is string => typeof item === 'string') : []),
  ]);
}

function sanitizeNarrativeValue(val: unknown): unknown {
  if (typeof val === 'string') {
    return /<!--\s*confidence:/i.test(val)
      ? val.replace(/<!--\s*confidence:\s*[^>-]+-->\s*/gi, '')
      : val;
  }
  if (Array.isArray(val)) return val.map(sanitizeNarrativeValue);
  if (isPlainObject(val)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(val)) {
      out[k] = sanitizeNarrativeValue(v);
    }
    return out;
  }
  return val;
}

function blocksForValue(sectionId: string, rawValue: unknown, index: number): ReportDocumentBlockV1[] {
  const value = sanitizeNarrativeValue(rawValue);
  const sourceIds = sourceIdsFromValue(value);
  const citationIds: string[] = [];
  const seed = `${sectionId}:${index}:${canonicalJson(value)}`;
  const id = `block-${sectionId}-${hashCanonicalJson(seed).slice(0, 16)}`;

  if (typeof value === 'string') {
    return [{ id, kind: 'narrative', markdown: value, sourceIds, citationIds }];
  }

  if (Array.isArray(value) && value.every((entry) => typeof entry === 'string')) {
    return [{
      id,
      kind: 'list',
      ordered: false,
      items: value.map((text, itemIndex) => ({
        id: `${id}-item-${itemIndex + 1}`,
        text,
      })),
      sourceIds,
      citationIds,
    }];
  }

  const projected = toJsonValue(value);
  if (projected === undefined) return [];
  return [{
    id,
    kind: 'structured-data',
    label: sectionId,
    value: projected,
    sourceIds,
    citationIds,
  }];
}

function sectionGroup(sectionId: string): DocumentSection['group'] {
  const lower = sectionId.toLowerCase();
  if (/appendix|source|blind|evidence|reference|legal/.test(lower)) return 'evidence';
  if (/risk|action|mitigation|conclusion|decision|path.?to.?go/.test(lower)) return 'risks-actions';
  if (/executive|summary|intent|context|overview/.test(lower)) return 'overview';
  return 'analysis';
}

function sectionRole(sectionId: string): DocumentSection['role'] {
  const lower = sectionId.toLowerCase();
  if (/appendix|source|blind|evidence|reference/.test(lower)) return 'appendix';
  if (/decision|conclusion|executive/.test(lower)) return 'decision';
  if (/map|layout.*exhibit/.test(lower)) return 'map';
  if (/chart|profile|sensitivity|montecarlo/.test(lower)) return 'chart';
  if (/matrix|comparison|breakdown|stream|ranking/.test(lower)) return 'table';
  if (/summary|overview|capacity|index/.test(lower)) return 'kpi';
  return 'narrative';
}

function frozenPresentationPolicy(
  contract: ReportSectionContract,
): DocumentSection['presentationPolicy'] {
  return {
    surfaceEligibility: { ...contract.surfaceEligibility },
    preferredSpan: contract.preferredSpan,
    splittable: contract.splittable,
    keepRules: { ...contract.keepRules },
    continuationLabel: contract.continuationLabel,
    expectedExhibits: [...contract.expectedExhibits],
    omissionPolicy: contract.omissionPolicy,
  };
}

function resolveSectionIds(report: SiteRagReport, reportType: ReportTypeId): string[] {
  const canonical = getSectionsForType(reportType, report)
    .map((section) => section.sectionId)
    .filter((sectionId) => (
      !NON_CONTENT_SECTION_IDS.has(sectionId)
      && (
        sectionId !== 'generationLayoutExhibit'
        || generationLayoutExhibitHasContent(reportRecord(report))
      )
    ));
  const canonicalSet = new Set(canonical);
  const persisted = (report.sectionOrder ?? [])
    .map((id) => migrateSectionIdForType(id, reportType))
    .filter((id) => !RETIRED_SECTION_IDS.has(id) && canonicalSet.has(id));
  // Section visibility is a presentation preference, never a semantic
  // completeness control. Dropping a hidden required section here made the
  // immutable model (and the legacy conversion gate) pass vacuously because
  // the missing section could no longer produce a required omission. Keep the
  // exhaustive canonical registry in the document; surface projectors may
  // apply an explicit, policy-accounted omission later.
  return uniqueStrings([...persisted, ...canonical]);
}

function buildCustomBriefQuestionSections(report: SiteRagReport): DocumentSection[] {
  if (report.reportType !== 'custom-brief' || !report.customBriefAnswers?.length) return [];
  return report.customBriefAnswers.map((answer, index) => {
    const sectionId = `customBriefQ_${answer.questionId || index + 1}`;
    const values = [answer.findings, answer.evidence, answer.redFlags].filter(hasContent);
    const title = answer.question || `Question ${index + 1}`;
    const contract = toReportSectionContract({
      sectionId,
      defaultLabel: title,
      page: 2,
      supportsColumnLayout: true,
    });
    return {
      id: `section-${sectionId}`,
      sectionId,
      title,
      group: 'analysis',
      role: 'narrative',
      required: true,
      presentationPolicy: frozenPresentationPolicy({
        ...contract,
        required: true,
        omissionPolicy: 'block-if-missing',
      }),
      blocks: values.flatMap((value, valueIndex) => blocksForValue(sectionId, value, valueIndex)),
    };
  });
}

function hasBlockSeverity(value: unknown, seen = new Set<object>()): boolean {
  if (!value || typeof value !== 'object') return false;
  if (seen.has(value)) return false;
  seen.add(value);
  if (Array.isArray(value)) return value.some((entry) => hasBlockSeverity(entry, seen));
  const row = value as Record<string, unknown>;
  if (row.severity === 'block') return true;
  return Object.values(row).some((entry) => hasBlockSeverity(entry, seen));
}

function buildQualityDecision(report: SiteRagReport): ReportDocumentModelV1['qualityDecision'] {
  const raw = reportRecord(report);
  const reviewerVerdict = report.qualityReview?.verdict ?? 'not-run';
  const deterministicValidated = hasValidatedDeterministicScreening(report);
  /*
   * SEVERITY POLICY (2026-08-05) — the reviewer is ADVISORY, so its verdict
   * alone must not change the document's deliverability state. `qualityHold`
   * is retained because `resolveQualityHold` now sets it ONLY on a
   * demonstrable truth violation. The verdict still travels on
   * `qualityDecision.reviewerVerdict` for display.
   */
  const hold = raw.qualityHold === 'needs-revision';
  // AUDIT-ADVISORY POLICY (2026-08-22, William) — truth violations no longer
  // fail the document either: they surface as answerable truth questions (the
  // recommended audit) and the hold stamp stays descriptive. Hard gates are
  // now ARTIFACT gates: only a metadata-only body fails them. The 2026-08-05
  // policy note above this (advisory findings never bar) still holds.
  const hardGatesPassed = !report._metadataOnly;
  const findings = uniqueStrings([
    ...(Array.isArray(raw.qualityHoldFindings)
      ? raw.qualityHoldFindings.filter((item): item is string => typeof item === 'string')
      : []),
    ...(report.qualityReview?.issues ?? []).map((issue) => `${issue.section}: ${issue.issue}`),
    ...(report.sanityChecks ?? [])
      .filter((check) => !check.passed && check.severity === 'error')
      .map((check) => check.message),
    ...(reviewerVerdict === 'not-run' && !deterministicValidated
      ? ['The final narrative reviewer did not produce a valid verdict.']
      : []),
  ]);

  let state: ReportDocumentModelV1['qualityDecision']['state'] = 'pass';
  if (!hardGatesPassed) state = 'failed';
  // A missing reviewer verdict outranks the hold: 'held' is now exportable
  // (audit-advisory), and a hold stamp must never UPGRADE an unreviewed
  // report past the no-verdict integrity bar (2026-08-22).
  else if (reviewerVerdict === 'not-run' && !deterministicValidated) state = 'degraded';
  // hold BEFORE draft: the hold era stamped isDraft=true as its export-bar
  // mirror, so a held report must read 'held', never 'draft' (2026-08-22).
  else if (hold) state = 'held';
  else if (report.generationTier === 'draft' || report.isDraft) state = 'draft';
  else if (report.degradedMode) state = 'degraded';

  return {
    state,
    hardGatesPassed,
    findings,
    reviewerVerdict,
    ...(deterministicValidated ? { validationMethod: 'deterministic-source-screening' as const } : {}),
  };
}

function resolveExpectedReportSlopePct(report: SiteRagReport): number | null {
  const parseFiniteSlope = (val: unknown): number | null => {
    if (typeof val === 'number') return Number.isFinite(val) ? val : null;
    if (typeof val === 'string') {
      const m = /^([-+]?\d+(?:\.\d+)?)\s*%?(?:\s+slope)?$/i.exec(val.trim());
      if (!m) return null;
      const n = Number(m[1]);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };
  const rawLandSlope = (report.datasets?.land as Record<string, unknown> | undefined)?.slope;
  if (typeof rawLandSlope === 'number' && Number.isFinite(rawLandSlope)) {
    return rawLandSlope;
  }
  const semEvVal = (
    report.decisionAnalysis as {
      semantic?: { evidence?: Record<string, { value?: unknown }> };
    } | undefined
  )?.semantic?.evidence?.['metric:site:topography']?.value;
  const semSlopeNum = parseFiniteSlope(semEvVal);
  if (semSlopeNum !== null) return semSlopeNum;
  const catSlopeVal = report.categories
    ?.find((c) => c.id === 'site')
    ?.metrics?.find((m) => m.id === 'topography')?.value;
  return parseFiniteSlope(catSlopeVal);
}

function extractTopographicCitationContext(
  raw: Record<string, unknown>,
  reportSiteId: string | undefined,
  expectedSlopePct: number | null,
): Partial<ReportDocumentModelV1['citations'][number]> {
  const rawField =
    (typeof raw.field === 'string' && raw.field.trim()) ||
    (typeof raw.sourceField === 'string' && raw.sourceField.trim()) ||
    (typeof raw.path === 'string' && raw.path.trim()) ||
    (typeof raw.metricId === 'string' && raw.metricId.trim()) ||
    (Array.isArray(raw.supportedFields)
      ? raw.supportedFields.find((f): f is string => typeof f === 'string' && f.trim().length > 0)?.trim()
      : undefined) ||
    (Array.isArray(raw.fields)
      ? raw.fields.find((f): f is string => typeof f === 'string' && f.trim().length > 0)?.trim()
      : undefined) ||
    (Array.isArray(raw.metrics)
      ? raw.metrics.find((f): f is string => typeof f === 'string' && f.trim().length > 0)?.trim()
      : undefined);

  const siteId =
    typeof raw.siteId === 'string' && raw.siteId.trim().length > 0
      ? raw.siteId.trim()
      : reportSiteId;

  const rawId =
    (typeof raw.id === 'string' && raw.id.trim()) ||
    (typeof raw.sourceId === 'string' && raw.sourceId.trim()) ||
    '';
  const documentId =
    (typeof raw.documentId === 'string' && raw.documentId.trim()) ||
    (typeof raw.docId === 'string' && raw.docId.trim()) ||
    (rawId ? rawId.replace(/^(?:doc|src|ref|citation):/i, '').trim() : '') ||
    undefined;

  const rawQual = typeof raw.qualification === 'string' ? raw.qualification.trim() : '';
  const qualification: ReportDocumentModelV1['citations'][number]['qualification'] =
    rawQual.length > 0
      ? (['verified', 'screening', 'estimated', 'unknown', 'unavailable'] as const).includes(
          rawQual as 'verified' | 'screening' | 'estimated' | 'unknown' | 'unavailable',
        )
        ? (rawQual as ReportDocumentModelV1['citations'][number]['qualification'])
        : 'unavailable'
      : undefined;

  const status = typeof raw.status === 'string' && raw.status.trim().length > 0 ? raw.status.trim() : undefined;
  const state = typeof raw.state === 'string' && raw.state.trim().length > 0 ? raw.state.trim() : undefined;
  const reviewState =
    typeof raw.reviewState === 'string' && raw.reviewState.trim().length > 0
      ? raw.reviewState.trim()
      : undefined;
  const coverageStatus =
    typeof raw.coverageStatus === 'string' && raw.coverageStatus.trim().length > 0
      ? raw.coverageStatus.trim()
      : undefined;

  const gradeRaw = raw.sourceGrade ?? raw.grade;
  const rawGradeStr = typeof gradeRaw === 'string' ? gradeRaw.trim() : '';
  const sourceGrade: ReportDocumentModelV1['citations'][number]['sourceGrade'] =
    rawGradeStr.length > 0
      ? ([
          'verified-professional',
          'authoritative-government',
          'reviewed-landgate',
          'regional-screening',
          'inference',
        ] as const).includes(
          rawGradeStr as
            | 'verified-professional'
            | 'authoritative-government'
            | 'reviewed-landgate'
            | 'regional-screening'
            | 'inference',
        )
        ? (rawGradeStr as ReportDocumentModelV1['citations'][number]['sourceGrade'])
        : 'inference'
      : undefined;

  const available = typeof raw.available === 'boolean' ? raw.available : undefined;
  const missing = typeof raw.missing === 'boolean' ? raw.missing : undefined;
  const notes = typeof raw.notes === 'string' && raw.notes.trim().length > 0 ? raw.notes.trim() : undefined;
  const summary = typeof raw.summary === 'string' && raw.summary.trim().length > 0 ? raw.summary.trim() : undefined;
  const description =
    typeof raw.description === 'string' && raw.description.trim().length > 0 ? raw.description.trim() : undefined;
  const excerpt = typeof raw.excerpt === 'string' && raw.excerpt.trim().length > 0 ? raw.excerpt.trim() : undefined;
  const excerptPreview =
    typeof raw.excerptPreview === 'string' && raw.excerptPreview.trim().length > 0
      ? raw.excerptPreview.trim()
      : undefined;
  const sourceQuote =
    typeof raw.sourceQuote === 'string' && raw.sourceQuote.trim().length > 0 ? raw.sourceQuote.trim() : undefined;
  const rationale =
    typeof raw.rationale === 'string' && raw.rationale.trim().length > 0 ? raw.rationale.trim() : undefined;
  const evidenceQuotes = Array.isArray(raw.evidenceQuotes)
    ? raw.evidenceQuotes.filter((q): q is string => typeof q === 'string' && q.trim().length > 0)
    : undefined;

  const rawAttr =
    raw.attribution && typeof raw.attribution === 'object' && !Array.isArray(raw.attribution)
      ? (raw.attribution as Record<string, unknown>)
      : undefined;
  const attribution = rawAttr
    ? {
        ...(typeof rawAttr.statedBy === 'string' ? { statedBy: rawAttr.statedBy } : {}),
        ...(typeof rawAttr.statedByRole === 'string' ? { statedByRole: rawAttr.statedByRole } : {}),
        ...(typeof rawAttr.organisation === 'string' ? { organisation: rawAttr.organisation } : {}),
        ...(typeof rawAttr.statedOn === 'string' ? { statedOn: rawAttr.statedOn } : {}),
        ...(rawAttr.appliesToProject != null && String(rawAttr.appliesToProject).trim().length > 0
          ? { appliesToProject: String(rawAttr.appliesToProject).trim() }
          : {}),
        ...(typeof rawAttr.projectName === 'string' ? { projectName: rawAttr.projectName } : {}),
      }
    : undefined;

  const demonstrationDisclosure =
    raw.demonstrationDisclosure !== undefined && raw.demonstrationDisclosure !== null
      ? typeof raw.demonstrationDisclosure === 'boolean' ||
        (typeof raw.demonstrationDisclosure === 'object' && !Array.isArray(raw.demonstrationDisclosure))
        ? (raw.demonstrationDisclosure as ReportDocumentModelV1['citations'][number]['demonstrationDisclosure'])
        : true
      : undefined;

  const rawSupportObj =
    raw.topographicSupport && typeof raw.topographicSupport === 'object' && !Array.isArray(raw.topographicSupport)
      ? (raw.topographicSupport as Record<string, unknown>)
      : raw.measurements && typeof raw.measurements === 'object' && !Array.isArray(raw.measurements)
        ? (raw.measurements as Record<string, unknown>)
        : undefined;

  const passesSupportGates = isRecordStructuredTopographySurveySupport(
    raw,
    reportSiteId,
    expectedSlopePct,
  );
  const topographicSupport: ReportDocumentModelV1['citations'][number]['topographicSupport'] =
    passesSupportGates &&
    rawSupportObj &&
    typeof rawSupportObj.measuredSlopePct === 'number' &&
    Number.isFinite(rawSupportObj.measuredSlopePct)
      ? {
          measuredSlopePct: rawSupportObj.measuredSlopePct,
          ...(typeof rawSupportObj.topographicContours === 'boolean'
            ? { topographicContours: rawSupportObj.topographicContours }
            : {}),
          ...(typeof rawSupportObj.contourIntervalFt === 'number' &&
          Number.isFinite(rawSupportObj.contourIntervalFt) &&
          rawSupportObj.contourIntervalFt >= 0
            ? { contourIntervalFt: rawSupportObj.contourIntervalFt }
            : {}),
          ...(typeof rawSupportObj.contourIntervalM === 'number' &&
          Number.isFinite(rawSupportObj.contourIntervalM) &&
          rawSupportObj.contourIntervalM >= 0
            ? { contourIntervalM: rawSupportObj.contourIntervalM }
            : {}),
          ...(typeof rawSupportObj.surveyPointsSampled === 'number' &&
          Number.isInteger(rawSupportObj.surveyPointsSampled) &&
          rawSupportObj.surveyPointsSampled >= 0
            ? { surveyPointsSampled: rawSupportObj.surveyPointsSampled }
            : {}),
          ...(typeof rawSupportObj.derivationMethod === 'string' &&
          [
            'field-topographic-survey',
            'alta-table-a-item-5-contours',
            'lidar-tin-grade',
            'civil-grading-survey',
          ].includes(rawSupportObj.derivationMethod)
            ? {
                derivationMethod: rawSupportObj.derivationMethod as NonNullable<
                  ReportDocumentModelV1['citations'][number]['topographicSupport']
                >['derivationMethod'],
              }
            : {}),
          ...(typeof rawSupportObj.siteId === 'string' && rawSupportObj.siteId.trim().length > 0
            ? { siteId: rawSupportObj.siteId.trim() }
            : {}),
        }
      : undefined;

  return {
    ...(rawField ? { sourceField: rawField } : {}),
    ...(siteId ? { siteId } : {}),
    ...(documentId ? { documentId } : {}),
    ...(qualification ? { qualification } : {}),
    ...(status ? { status } : {}),
    ...(state ? { state } : {}),
    ...(reviewState ? { reviewState } : {}),
    ...(coverageStatus ? { coverageStatus } : {}),
    ...(sourceGrade ? { sourceGrade } : {}),
    ...(available !== undefined ? { available } : {}),
    ...(missing !== undefined ? { missing } : {}),
    ...(notes ? { notes } : {}),
    ...(summary ? { summary } : {}),
    ...(description ? { description } : {}),
    ...(excerpt ? { excerpt } : {}),
    ...(excerptPreview ? { excerptPreview } : {}),
    ...(sourceQuote ? { sourceQuote } : {}),
    ...(rationale ? { rationale } : {}),
    ...(evidenceQuotes && evidenceQuotes.length > 0 ? { evidenceQuotes } : {}),
    ...(attribution && Object.keys(attribution).length > 0 ? { attribution } : {}),
    ...(demonstrationDisclosure !== undefined ? { demonstrationDisclosure } : {}),
    ...(topographicSupport ? { topographicSupport } : {}),
  };
}

const TOPOGRAPHY_CITATION_FIELD_SET = new Set([
  'land.slope',
  'slope',
  'topography',
  'topography.slope',
  'site.topography',
  'metric:site:topography',
]);

const DISQUALIFYING_CITATION_STATE_SET = new Set([
  'unavailable',
  'missing',
  'unknown',
  'unverified',
  'conflict',
  'unverified-conflict',
  'failed',
  'rejected',
  'skipped',
  'empty',
  'anomalous',
  'screening',
  'estimated',
  'draft',
  'held',
  'degraded',
  'pending-review',
]);

const VERIFIED_CITATION_STATE_SET = new Set(['verified', 'verified-from-doc']);

const AUTOMATED_CITATION_PROXY_RE =
  /\b(?:google[-_\s]*elevation|usgs[-_\s]*epqs|elevation[-_\s]*proxy|elevation[-_\s]*relief|refdata[-_\s]*3dep|3dep[-_\s]*regional|polygon[-_\s]*sample|deterministic\s+elevation[-_\s]*relief)\b/i;

function isDisqualifyingTopographyText(text: string): boolean {
  return NEGATED_OR_MISSING_TOPOGRAPHY_RE.test(text) || AUTOMATED_CITATION_PROXY_RE.test(text);
}

function sortCitationTextsOrderIndependent(values: readonly string[]): string[] {
  return uniqueStrings(values).sort((a, b) => {
    const da = isDisqualifyingTopographyText(a) ? 0 : 1;
    const db = isDisqualifyingTopographyText(b) ? 0 : 1;
    if (da !== db) return da - db;
    return a.localeCompare(b);
  });
}

function mergeTopographicCitationContextFromGroup(
  groupRecords: readonly Record<string, unknown>[],
  reportSiteId: string | undefined,
  expectedSlopePct: number | null,
): Partial<ReportDocumentModelV1['citations'][number]> {
  const hasAnySupportOrMeasurements = groupRecords.some(
    (r) => r.topographicSupport !== undefined || r.measurements !== undefined,
  );
  if (!hasAnySupportOrMeasurements) return {};

  const individualContexts = groupRecords.map((r) =>
    extractTopographicCitationContext(r, reportSiteId, expectedSlopePct),
  );
  const allRecordsPass =
    groupRecords.length > 0 &&
    groupRecords.every((r) =>
      isRecordStructuredTopographySurveySupport(r, reportSiteId, expectedSlopePct),
    );

  // 1. sourceField: preserve non-topography field if any record has a conflicting field
  const allFields = uniqueStrings(
    individualContexts
      .map((c) => c.sourceField)
      .filter((f): f is string => typeof f === 'string' && f.trim().length > 0),
  );
  const nonTopoFields = allFields
    .filter((f) => !TOPOGRAPHY_CITATION_FIELD_SET.has(f.toLowerCase()))
    .sort((a, b) => a.localeCompare(b));
  const topoFields = allFields
    .filter((f) => TOPOGRAPHY_CITATION_FIELD_SET.has(f.toLowerCase()))
    .sort((a, b) => a.localeCompare(b));
  const sourceField = nonTopoFields[0] ?? topoFields[0];

  // 2. siteId: preserve foreign siteId if any record (or its topographicSupport) has a foreign siteId
  const rawSiteIds: string[] = [];
  for (const r of groupRecords) {
    if (typeof r.siteId === 'string' && r.siteId.trim().length > 0) {
      rawSiteIds.push(r.siteId.trim());
    }
    const sup = (r.topographicSupport ?? r.measurements) as Record<string, unknown> | undefined;
    if (sup && typeof sup === 'object' && typeof sup.siteId === 'string' && sup.siteId.trim().length > 0) {
      rawSiteIds.push(sup.siteId.trim());
    }
  }
  const foreignSiteIds = uniqueStrings(rawSiteIds)
    .filter((id) => id !== reportSiteId)
    .sort((a, b) => a.localeCompare(b));
  const siteId = foreignSiteIds[0] ?? reportSiteId;

  // 3. documentId: deterministic lexicographical pick
  const docIds = uniqueStrings(
    individualContexts
      .map((c) => c.documentId)
      .filter((d): d is string => typeof d === 'string' && d.trim().length > 0),
  ).sort((a, b) => a.localeCompare(b));
  const documentId = docIds[0];

  // 4. qualification: any disqualifying qualification wins deterministically
  const qualPriority: Array<NonNullable<ReportDocumentModelV1['citations'][number]['qualification']>> = [
    'unavailable',
    'unknown',
    'estimated',
    'screening',
    'verified',
  ];
  const presentQuals = new Set(
    individualContexts
      .map((c) => c.qualification)
      .filter((q): q is NonNullable<typeof q> => q !== undefined),
  );
  const hasRecordMissingVerifiedState = groupRecords.some((r) => {
    const q = typeof r.qualification === 'string' ? r.qualification.trim().toLowerCase() : '';
    const st = typeof r.status === 'string' ? r.status.trim().toLowerCase() : '';
    return !VERIFIED_CITATION_STATE_SET.has(q) && !VERIFIED_CITATION_STATE_SET.has(st);
  });
  let qualification = qualPriority.find((q) => presentQuals.has(q));
  if (qualification === 'verified' && hasRecordMissingVerifiedState) {
    qualification = 'unknown';
  }

  // 5. status, state, reviewState, coverageStatus: any disqualifying state wins deterministically
  const pickStateField = (
    getter: (c: Partial<ReportDocumentModelV1['citations'][number]>) => string | undefined,
    requireVerified = false,
  ): string | undefined => {
    const vals = uniqueStrings(
      individualContexts
        .map(getter)
        .filter((v): v is string => typeof v === 'string' && v.trim().length > 0),
    );
    if (vals.length === 0) return undefined;
    const disq = vals
      .filter(
        (v) =>
          DISQUALIFYING_CITATION_STATE_SET.has(v.toLowerCase()) ||
          (requireVerified && !VERIFIED_CITATION_STATE_SET.has(v.toLowerCase())),
      )
      .sort((a, b) => a.localeCompare(b));
    if (disq.length > 0) return disq[0];
    return [...vals].sort((a, b) => a.localeCompare(b))[0];
  };
  const status = pickStateField((c) => c.status, true);
  const state = pickStateField((c) => c.state);
  let reviewState = pickStateField((c) => c.reviewState);
  const coverageStatus = pickStateField((c) => c.coverageStatus);

  // 6. sourceGrade: any non-allowed grade wins deterministically
  const gradePriority: Array<NonNullable<ReportDocumentModelV1['citations'][number]['sourceGrade']>> = [
    'inference',
    'regional-screening',
    'reviewed-landgate',
    'authoritative-government',
    'verified-professional',
  ];
  const presentGrades = new Set(
    individualContexts
      .map((c) => c.sourceGrade)
      .filter((g): g is NonNullable<typeof g> => g !== undefined),
  );
  const sourceGrade = gradePriority.find((g) => presentGrades.has(g));

  // 7. available & missing: available:false and missing:true always win
  const available = individualContexts.some((c) => c.available === false)
    ? false
    : individualContexts.some((c) => c.available === true)
      ? true
      : undefined;
  const missing = individualContexts.some((c) => c.missing === true)
    ? true
    : individualContexts.some((c) => c.missing === false)
      ? false
      : undefined;

  // 8. Text fields: join distinct values in deterministic order (disqualifying/negated first)
  const mergeTextField = (
    getter: (c: Partial<ReportDocumentModelV1['citations'][number]>) => string | undefined,
  ): string | undefined => {
    const sorted = sortCitationTextsOrderIndependent(
      individualContexts
        .map(getter)
        .filter((v): v is string => typeof v === 'string' && v.trim().length > 0),
    );
    return sorted.length > 0 ? sorted.join(' | ') : undefined;
  };
  const notes = mergeTextField((c) => c.notes);
  const summary = mergeTextField((c) => c.summary);
  const description = mergeTextField((c) => c.description);
  const excerpt = mergeTextField((c) => c.excerpt);
  const excerptPreview = mergeTextField((c) => c.excerptPreview);
  const sourceQuote = mergeTextField((c) => c.sourceQuote);
  const rationale = mergeTextField((c) => c.rationale);
  const sortedQuotes = sortCitationTextsOrderIndependent(
    individualContexts.flatMap((c) => c.evidenceQuotes ?? []),
  );
  const evidenceQuotes = sortedQuotes.length > 0 ? sortedQuotes : undefined;

  // 9. attribution: any foreign appliesToProject wins deterministically
  const attrList = individualContexts
    .map((c) => c.attribution)
    .filter((a): a is NonNullable<typeof a> => a !== undefined && Object.keys(a).length > 0);
  let attribution: ReportDocumentModelV1['citations'][number]['attribution'];
  if (attrList.length > 0) {
    const appliesVals = uniqueStrings(
      attrList
        .map((a) => a.appliesToProject)
        .filter((v): v is string => typeof v === 'string' && v.trim().length > 0),
    );
    const disqApplies = appliesVals
      .filter((v) => v !== 'this-site' && v !== reportSiteId)
      .sort((a, b) => a.localeCompare(b));
    const okApplies = appliesVals
      .filter((v) => !disqApplies.includes(v))
      .sort((a, b) => a.localeCompare(b));
    const appliesToProject = disqApplies[0] ?? okApplies[0];
    const pickFirstAttr = (key: 'statedBy' | 'statedByRole' | 'organisation' | 'statedOn' | 'projectName') => {
      const vals = uniqueStrings(
        attrList.map((a) => a[key]).filter((v): v is string => typeof v === 'string' && v.trim().length > 0),
      ).sort((a, b) => a.localeCompare(b));
      return vals[0];
    };
    const statedBy = pickFirstAttr('statedBy');
    const statedByRole = pickFirstAttr('statedByRole');
    const organisation = pickFirstAttr('organisation');
    const statedOn = pickFirstAttr('statedOn');
    const projectName = pickFirstAttr('projectName');
    attribution = {
      ...(statedBy ? { statedBy } : {}),
      ...(statedByRole ? { statedByRole } : {}),
      ...(organisation ? { organisation } : {}),
      ...(statedOn ? { statedOn } : {}),
      ...(appliesToProject ? { appliesToProject } : {}),
      ...(projectName ? { projectName } : {}),
    };
  }

  // 10. demonstrationDisclosure: any disclosure wins deterministically
  const demoDisclosures = individualContexts
    .map((c) => c.demonstrationDisclosure)
    .filter((d): d is NonNullable<typeof d> => d !== undefined);
  let demonstrationDisclosure: ReportDocumentModelV1['citations'][number]['demonstrationDisclosure'];
  if (demoDisclosures.length > 0) {
    const objDisclosures = demoDisclosures
      .filter((d): d is Exclude<typeof d, boolean> => typeof d === 'object' && d !== null)
      .sort((a, b) => canonicalJson(a).localeCompare(canonicalJson(b)));
    demonstrationDisclosure = objDisclosures[0] ?? true;
  }

  // 11. topographicSupport: emit only when every record in the group passes and all measured slopes agree
  const measuredSlopes = groupRecords
    .map((r) => {
      const sup = (r.topographicSupport ?? r.measurements) as Record<string, unknown> | undefined;
      return sup && typeof sup === 'object' && typeof sup.measuredSlopePct === 'number' && Number.isFinite(sup.measuredSlopePct)
        ? sup.measuredSlopePct
        : null;
    })
    .filter((n): n is number => n !== null);
  const slopesAgree =
    measuredSlopes.length > 0 &&
    Math.max(...measuredSlopes) - Math.min(...measuredSlopes) < 1e-6;

  const validSupports = individualContexts
    .map((c) => c.topographicSupport)
    .filter((s): s is NonNullable<typeof s> => s !== undefined)
    .sort((a, b) => canonicalJson(a).localeCompare(canonicalJson(b)));

  const candidateMergedContext: Partial<ReportDocumentModelV1['citations'][number]> = {
    ...(sourceField ? { sourceField } : {}),
    ...(siteId ? { siteId } : {}),
    ...(documentId ? { documentId } : {}),
    ...(qualification ? { qualification } : {}),
    ...(status ? { status } : {}),
    ...(state ? { state } : {}),
    ...(reviewState ? { reviewState } : {}),
    ...(coverageStatus ? { coverageStatus } : {}),
    ...(sourceGrade ? { sourceGrade } : {}),
    ...(available !== undefined ? { available } : {}),
    ...(missing !== undefined ? { missing } : {}),
    ...(notes ? { notes } : {}),
    ...(summary ? { summary } : {}),
    ...(description ? { description } : {}),
    ...(excerpt ? { excerpt } : {}),
    ...(excerptPreview ? { excerptPreview } : {}),
    ...(sourceQuote ? { sourceQuote } : {}),
    ...(rationale ? { rationale } : {}),
    ...(evidenceQuotes ? { evidenceQuotes } : {}),
    ...(attribution && Object.keys(attribution).length > 0 ? { attribution } : {}),
    ...(demonstrationDisclosure !== undefined ? { demonstrationDisclosure } : {}),
  };

  let topographicSupport: ReportDocumentModelV1['citations'][number]['topographicSupport'];
  if (allRecordsPass && slopesAgree && validSupports.length > 0) {
    const chosenSupport = validSupports[0]!;
    if (
      isRecordStructuredTopographySurveySupport(
        { ...candidateMergedContext, topographicSupport: chosenSupport },
        reportSiteId,
        expectedSlopePct,
      )
    ) {
      topographicSupport = chosenSupport;
    }
  }

  // If the group failed support gates (e.g. slope mismatch or missing support on one duplicate)
  // and no other field on candidateMergedContext would fail Gate 1/2/3/5 if topographicSupport
  // were re-attached, stamp reviewState='conflict' so conflict provenance is preserved.
  if (!topographicSupport && validSupports.length > 0) {
    const wouldPassIfSupportReattached = isRecordStructuredTopographySurveySupport(
      { ...candidateMergedContext, topographicSupport: validSupports[0] },
      reportSiteId,
      expectedSlopePct,
    );
    if (wouldPassIfSupportReattached && !reviewState) {
      reviewState = 'conflict';
      candidateMergedContext.reviewState = 'conflict';
    }
  }

  return {
    ...candidateMergedContext,
    ...(topographicSupport ? { topographicSupport } : {}),
  };
}

function buildCitations(report: SiteRagReport): ReportDocumentModelV1['citations'] {
  const expectedSlopePct = resolveExpectedReportSlopePct(report);
  const allAdmissionRows = admitReportSources(report);
  const semanticSources = (
    report.decisionAnalysis as {
      semantic?: { sources?: Record<string, Record<string, unknown>> | Array<Record<string, unknown>> };
    } | undefined
  )?.semantic?.sources;
  const semanticSourceList: Array<Record<string, unknown>> = Array.isArray(semanticSources)
    ? semanticSources.filter(
        (s): s is Record<string, unknown> => Boolean(s && typeof s === 'object' && !Array.isArray(s)),
      )
    : semanticSources && typeof semanticSources === 'object'
      ? Object.entries(semanticSources).flatMap(([k, v]) =>
          v && typeof v === 'object' && !Array.isArray(v)
            ? [{ id: k, ...(v as Record<string, unknown>) }]
            : [],
        )
      : [];

  const stripPrefix = (val: string): string =>
    val.replace(/^(?:doc|src|ref|citation|asset):/i, '').trim();

  interface SourcePoolNode {
    readonly poolIndex: number;
    readonly kind: 'ref' | 'sem';
    readonly rec: Record<string, unknown>;
    readonly refIdx?: number;
    readonly rawId: string;
    readonly explicitDocId: string;
    readonly docKeys: ReadonlySet<string>;
    readonly refOrdinals: ReadonlySet<number>;
    readonly title: string;
    readonly hasTopo: boolean;
  }

  const buildPoolNode = (
    poolIndex: number,
    kind: 'ref' | 'sem',
    r: Record<string, unknown>,
    explicitRefIdx?: number,
  ): SourcePoolNode => {
    const rawId =
      (typeof r.id === 'string' && r.id.trim()) ||
      (typeof r.sourceId === 'string' && r.sourceId.trim()) ||
      '';
    const explicitDocId =
      (typeof r.documentId === 'string' && r.documentId.trim()) ||
      (typeof r.docId === 'string' && r.docId.trim()) ||
      '';
    const docKeys = new Set<string>();
    const refOrdinals = new Set<number>();
    if (explicitRefIdx !== undefined) {
      refOrdinals.add(explicitRefIdx);
    }
    for (const cand of [r.id, r.sourceId, r.documentId, r.docId, r.citationId, r.refId]) {
      if (typeof cand !== 'string') continue;
      const t = cand.trim();
      if (!t) continue;
      const rm = /^(?:citation[-_:]?)?ref[-_:]?(\d+)$/i.exec(t);
      if (rm) {
        refOrdinals.add(Number(rm[1]));
      } else {
        docKeys.add(t);
        const b = stripPrefix(t);
        if (b) docKeys.add(b);
      }
    }
    const title =
      (typeof r.title === 'string' && r.title.trim()) ||
      (typeof r.source === 'string' && r.source.trim()) ||
      (typeof r.label === 'string' && r.label.trim()) ||
      (typeof r.name === 'string' && r.name.trim()) ||
      '';
    const hasTopo = r.topographicSupport !== undefined || r.measurements !== undefined;
    return {
      poolIndex,
      kind,
      rec: r,
      refIdx: explicitRefIdx,
      rawId,
      explicitDocId,
      docKeys,
      refOrdinals,
      title,
      hasTopo,
    };
  };

  const poolNodes: SourcePoolNode[] = [];
  const refRowNodeMap = new Map<(typeof allAdmissionRows)[number], SourcePoolNode>();
  for (const row of allAdmissionRows) {
    const refRec = row.reference as unknown as Record<string, unknown>;
    const node = buildPoolNode(poolNodes.length, 'ref', refRec, row.index);
    poolNodes.push(node);
    refRowNodeMap.set(row, node);
  }
  for (const s of semanticSourceList) {
    const node = buildPoolNode(poolNodes.length, 'sem', s, undefined);
    poolNodes.push(node);
  }

  const nodesDirectlyConnect = (u: SourcePoolNode, v: SourcePoolNode): boolean => {
    for (const k of u.docKeys) {
      if (v.docKeys.has(k)) return true;
    }
    let sharesOrdinal = false;
    for (const ord of u.refOrdinals) {
      if (v.refOrdinals.has(ord)) {
        sharesOrdinal = true;
        break;
      }
    }
    if (sharesOrdinal) {
      if (
        u.explicitDocId &&
        v.explicitDocId &&
        stripPrefix(u.explicitDocId) !== stripPrefix(v.explicitDocId)
      ) {
        return false;
      }
      const bothHaveTopo = u.hasTopo && v.hasTopo;
      const anyHasTopo = u.hasTopo || v.hasTopo;
      const titlesCompatible =
        bothHaveTopo || !u.title || !v.title || u.title === v.title;
      return anyHasTopo && titlesCompatible;
    }
    return false;
  };

  interface EvaluatedSourceComponent {
    readonly nodes: readonly SourcePoolNode[];
    readonly records: readonly Record<string, unknown>[];
    readonly hasTopoSupport: boolean;
    readonly mergedCtx: Partial<ReportDocumentModelV1['citations'][number]>;
    readonly semanticRawIds: readonly string[];
    readonly canonicalGroupId: string;
    readonly label: string;
  }

  // Partition all finite source records into connected components via fixed-point convergence
  // over visitedPoolIndices BEFORE emitting any citation.
  const visitedPoolIndices = new Set<number>();
  const evaluatedComponents: EvaluatedSourceComponent[] = [];
  const componentByPoolIndex = new Map<number, EvaluatedSourceComponent>();

  for (let i = 0; i < poolNodes.length; i += 1) {
    if (visitedPoolIndices.has(i)) continue;
    const compIndices = new Set<number>([i]);
    visitedPoolIndices.add(i);
    let changed = true;
    while (changed) {
      changed = false;
      for (let j = 0; j < poolNodes.length; j += 1) {
        if (compIndices.has(j)) continue;
        for (const memberIdx of compIndices) {
          if (nodesDirectlyConnect(poolNodes[memberIdx]!, poolNodes[j]!)) {
            compIndices.add(j);
            visitedPoolIndices.add(j);
            changed = true;
            break;
          }
        }
      }
    }

    const compNodes = [...compIndices].map((idx) => poolNodes[idx]!);
    const compRecords = compNodes.map((n) => n.rec);
    const hasTopoSupport = compNodes.some((n) => n.hasTopo);
    const mergedCtx = hasTopoSupport
      ? mergeTopographicCitationContextFromGroup(
          compRecords,
          report.siteId,
          expectedSlopePct,
        )
      : {};
    const semanticRawIds = uniqueStrings(
      compNodes
        .filter((n) => n.kind === 'sem')
        .map((n) => n.rawId)
        .filter((id) => id.length > 0),
    ).sort((a, b) => a.localeCompare(b));
    const allRawIds = uniqueStrings(
      compNodes
        .map((n) => n.rawId)
        .filter((id) => id.length > 0),
    ).sort((a, b) => a.localeCompare(b));
    const canonicalGroupId =
      mergedCtx.documentId ?? semanticRawIds[0] ?? allRawIds[0] ?? '';
    const sortedLabels = sortCitationTextsOrderIndependent(
      compNodes.map((n) => n.title).filter((t) => t.length > 0),
    );
    const label = sortedLabels.length > 0 ? sortedLabels.join(' | ') : canonicalGroupId;

    const evaluated: EvaluatedSourceComponent = {
      nodes: compNodes,
      records: compRecords,
      hasTopoSupport,
      mergedCtx,
      semanticRawIds,
      canonicalGroupId,
      label,
    };
    evaluatedComponents.push(evaluated);
    for (const idx of compIndices) {
      componentByPoolIndex.set(idx, evaluated);
    }
  }

  const citations: ReportDocumentModelV1['citations'] = allAdmissionRows
    .filter((row) => row.admitted)
    .map((row) => {
      const { reference, index } = row;
      const url = reference.url?.trim();
      const publicUrl = Boolean(
        url &&
          /^https:\/\//i.test(url) &&
          !/localhost|127\.0\.0\.1|\.internal(?:\/|$)/i.test(url),
      );
      const node = refRowNodeMap.get(row)!;
      const comp = componentByPoolIndex.get(node.poolIndex)!;
      return {
        id: `citation-ref-${index}`,
        label: reference.source || reference.field || `Reference ${index}`,
        ...(publicUrl ? { url } : {}),
        ...(reference.retrievedAt ? { retrievedAt: reference.retrievedAt } : {}),
        ...(reference.field ? { sourceField: reference.field } : {}),
        public: publicUrl,
        ...(comp.hasTopoSupport ? comp.mergedCtx : {}),
      };
    });

  // Emit pre-evaluated semantic-source components with topographicSupport context in
  // deterministic canonicalGroupId order after full connected-component convergence.
  const existingIds = new Set(
    citations.flatMap((c) => [c.id, ...(c.documentId ? [c.documentId] : [])]),
  );

  const semanticTopoComponents = evaluatedComponents
    .filter((comp) => comp.hasTopoSupport && comp.semanticRawIds.length > 0 && comp.canonicalGroupId.length > 0)
    .sort((a, b) => a.canonicalGroupId.localeCompare(b.canonicalGroupId));

  for (const comp of semanticTopoComponents) {
    const idsToEmit =
      comp.semanticRawIds.length > 0 ? comp.semanticRawIds : [comp.canonicalGroupId];
    for (const emitId of idsToEmit) {
      const refMatch = /^(?:citation[-_:]?)?ref[-_:]?(\d+)$/i.exec(emitId);
      if (refMatch && existingIds.has(`citation-ref-${refMatch[1]}`)) {
        continue;
      }
      if (existingIds.has(emitId)) continue;
      citations.push({
        id: emitId,
        label: comp.label,
        public: false,
        ...comp.mergedCtx,
      });
      existingIds.add(emitId);
    }
  }

  return citations;
}

function buildClaims(
  report: SiteRagReport,
  publicProjection: boolean,
): ReportDocumentModelV1['claims'] {
  return (report.claims ?? []).filter((claim) => claim.text.trim()).map((claim, index) => {
    const sectionId = claim.sectionId ?? claim.reportSectionId ?? 'unassigned';
    const allSourceIds = uniqueStrings(claim.sources ?? []);
    const sourceIds = publicProjection ? publicSafeSourceIds(allSourceIds) : allSourceIds;
    const citationIds = sourceIds
      .map((source) => /^ref:(\d+)$/.exec(source)?.[1])
      .filter((id): id is string => Boolean(id))
      .map((id) => `citation-ref-${id}`);
    return {
      id: claim.id ?? `claim-${hashCanonicalJson({ sectionId, text: claim.text, index }).slice(0, 16)}`,
      sectionId,
      text: claim.text,
      sourceIds,
      citationIds,
    };
  });
}

function buildEvidence(
  report: SiteRagReport,
  publicProjection: boolean,
): Record<string, EvidenceValue> {
  const evidence: Record<string, EvidenceValue> = {};
  const briefFrame = report.reportType === 'custom-brief' && !publicProjection
    ? customBriefSourceFrame(report) : null;
  if (briefFrame && briefFrame !== 'invalid') {
    // The exact generated-report provenance belongs to private semantic
    // identity. Citation labels alone omit its immutable source hashes.
    evidence[CUSTOM_BRIEF_SOURCE_FRAME_FIELD] = {
      state: 'known', value: toJsonValue(briefFrame)!, sourceIds: [],
    };
  }
  const hasAnyTopoSupportSource = (() => {
    for (const ref of (report.references ?? []) as unknown as Array<Record<string, unknown>>) {
      if (ref && typeof ref === 'object' && (ref.topographicSupport !== undefined || ref.measurements !== undefined)) {
        return true;
      }
    }
    const semSrcs = (
      report.decisionAnalysis as {
        semantic?: { sources?: Record<string, Record<string, unknown>> | Array<Record<string, unknown>> };
      } | undefined
    )?.semantic?.sources;
    const semList = Array.isArray(semSrcs)
      ? semSrcs
      : semSrcs && typeof semSrcs === 'object'
        ? Object.values(semSrcs)
        : [];
    for (const s of semList) {
      if (s && typeof s === 'object' && (s.topographicSupport !== undefined || s.measurements !== undefined)) {
        return true;
      }
    }
    return false;
  })();
  for (const category of report.categories ?? []) {
    for (const metric of category.metrics ?? []) {
      const id = `metric:${category.id}:${metric.id}`;
      const rawSemanticTopoSourceIds =
        id === 'metric:site:topography'
        && hasAnyTopoSupportSource
        && Array.isArray(
          (
            report.decisionAnalysis as {
              semantic?: { evidence?: Record<string, { sourceIds?: unknown[] }> };
            } | undefined
          )?.semantic?.evidence?.['metric:site:topography']?.sourceIds,
        )
          ? (
              (
                report.decisionAnalysis as {
                  semantic?: { evidence?: Record<string, { sourceIds?: unknown[] }> };
                }
              ).semantic!.evidence!['metric:site:topography']!.sourceIds ?? []
            ).filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
          : [];
      const allSourceIds = uniqueStrings([
        ...(metric.contributingDocIds ?? []).map((docId) => `doc:${docId}`),
        ...(metric.provenance ? [`provenance:${metric.provenance}`] : []),
        ...rawSemanticTopoSourceIds,
      ]);
      const sourceIds = publicProjection ? publicSafeSourceIds(allSourceIds) : allSourceIds;
      if (metric.missing || metric.value === null || metric.value === undefined) {
        evidence[id] = {
          state: 'unknown',
          reason: metric.missing
            ? 'The source report marked this metric as missing evidence.'
            : 'No metric value was persisted.',
          ...(metric.unit ? { unit: metric.unit } : {}),
          sourceIds,
        };
      } else {
        const value = toJsonValue(metric.value);
        evidence[id] = value === undefined
          ? {
              state: 'unknown',
              reason: 'The metric value was not a finite JSON value.',
              ...(metric.unit ? { unit: metric.unit } : {}),
              sourceIds,
            }
          : {
              state: 'known',
              value,
              ...(metric.unit ? { unit: metric.unit } : {}),
              sourceIds,
            };
      }
    }
  }
  return evidence;
}

function buildAssets(
  report: SiteRagReport,
  publicProjection: boolean,
): ReportDocumentModelV1['assets'] {
  return (report.documents ?? [])
    .filter((document) => document.includeInReport && !publicProjection)
    .map((document) => {
      const contentHash = document.contentHash?.trim().toLowerCase();
      if (!contentHash || !/^[a-f0-9]{64}$/.test(contentHash)) {
        throw new Error(
          `Included report document ${document.id} cannot be frozen without an exact SHA-256 contentHash.`,
        );
      }
      return {
        id: `asset-${document.id}`,
        kind: document.mimeType.startsWith('image/') ? 'image' as const : 'attachment' as const,
        label: document.fileName,
        ...(document.rawStoragePath ? { path: document.rawStoragePath } : {}),
        sha256: contentHash,
        mediaType: document.mimeType,
        altText: report.figureCaptions?.[document.id]?.alt ?? document.fileName,
        public: false,
      };
    });
}

export interface BuildReportDocumentV1Options {
  /** Public projections omit uploaded asset paths and private document bodies. */
  publicProjection?: boolean;
  /** Exact, already-hashed non-document assets selected for this revision.
   * Their storage locator is filled only after the semantic revision identity
   * is known; `path` is therefore intentionally optional here. */
  additionalAssets?: readonly ReportDocumentModelV1['assets'][number][];
}

/**
 * Deterministically project the legacy/raw SiteRagReport into the immutable
 * semantic document contract. No provider call, live SED read or layout logic
 * is permitted here.
 */
export function buildReportDocumentModelV1(
  reportInput: SiteRagReport,
  options: BuildReportDocumentV1Options = {},
): ReportDocumentModelV1 {
  const demonstrationDisclosure = reportDemonstrationDisclosure(reportInput);
  // Read only the validated public-safe carrier from the frozen report. All
  // private document/quote/hash witnesses and other diligence rows stay out.
  // The frozen regulatory screen contributes ONE appendix block holding the
  // result only (never its private input); absent on legacy reports.
  const resourceBlocks = [...buildSiteResourceDocumentBlocks(reportInput.siteDiligenceEvidence?.resourceStatements), ...buildCampusResourceAdvisoryDocumentBlocks(readCampusResourceAdvisory(reportInput)),
    ...buildRegulatoryFeasibilityDocumentBlocks(readRegulatoryFeasibilityScreen(reportInput))];
  const report = withDerivedTableReferences(projectReportSourceEvidence(reportInput));
  /*
   * WITHHELD NARRATIVE (2026-09-18). A leaf the reviewer-voice gate withheld
   * is null on the body, with its path recorded on the report. The frozen
   * document must neither lose it without trace nor count it as content:
   *   - a section whose ONLY content was withheld is an explicit omission
   *     whose detail says so (never "no citable content", which claimed an
   *     evidence gap nobody measured), so a required one still fails
   *     completeness;
   *   - a section with other content keeps the withheld leaf visible IN
   *     PLACE as the "not assessed — treat as open" notice;
   *   - a withheld top-level `summary` freezes as that notice too, not '' (the
   *     public projection and every complete-record appendix read
   *     `document.summary`; the deck printed '' as "Not recorded").
   * Deterministic in the stored body (the record travels on it), and the
   * identity for every report without a withheld record.
   */
  const presented = materializeWithheldNarrative(report);
  const reportType = report.reportType ?? 'feasibility';
  const publicProjection = options.publicProjection === true;
  const omissions: DocumentOmission[] = [];
  const sectionContracts = new Map(
    getSectionContractsForType(reportType, report).map((section) => [section.sectionId, section]),
  );
  const sections = resolveSectionIds(report, reportType).map((sectionId): DocumentSection => {
    const title = report.headingOverrides?.[sectionId] ?? getSectionLabel(sectionId, reportType);
    const sectionContract = sectionContracts.get(sectionId) ?? toReportSectionContract({
      sectionId,
      defaultLabel: title,
      page: 2,
      supportsColumnLayout: true,
    });
    const coverageOnlyOmission = hasCoverageOnlySentimentOmission(report, sectionId) || hasStanceOnlySentimentOmission(report, sectionId);
    const required = coverageOnlyOmission ? false : (sectionContract?.required ?? !OPTIONAL_SECTION_IDS.has(sectionId));
    const values = findSectionValues(report, sectionId, reportType);
    const sourceBlocks = values.flatMap((value, index) => blocksForValue(sectionId, value, index));
    const blocks = presented === report || sourceBlocks.length === 0
      ? sourceBlocks
      : findSectionValues(presented, sectionId, reportType)
        .flatMap((value, index) => blocksForValue(sectionId, value, index));
    const withheldOnly = sourceBlocks.length === 0
      && presented !== report
      && findSectionValues(presented, sectionId, reportType).length > 0;
    if (sectionId === 'appendix' && resourceBlocks.length) {
      // Retain the original withheld notice alongside this independent safe
      // resource subset; it never restores the unavailable narrative.
      if (withheldOnly) blocks.push(...findSectionValues(presented, sectionId, reportType)
        .flatMap((value, index) => blocksForValue(sectionId, value, index)));
      blocks.push(...resourceBlocks);
    }
    if (blocks.length === 0) {
      omissions.push({
        sectionId,
        // A withheld-only section is not an evidence gap: its narrative
        // SOURCE is unavailable in this report. (No contract change: an
        // existing reason, with honest detail.)
        reason: report._metadataOnly || withheldOnly ? 'source-unavailable' : 'insufficient-evidence',
        detail: report._metadataOnly
          ? 'The full GCS report body is unavailable; metadata cannot substitute for section content.'
          : withheldOnly
            ? WITHHELD_SECTION_OMISSION_DETAIL
            : 'No citable content was present in the frozen source report.',
        required,
      });
    }
    return {
      id: `section-${sectionId}`,
      sectionId,
      title,
      group: sectionContract?.group ?? sectionGroup(sectionId),
      role: sectionContract?.semanticRole ?? sectionRole(sectionId),
      required,
      presentationPolicy: { ...frozenPresentationPolicy(sectionContract), ...(coverageOnlyOmission ? { omissionPolicy: 'record-explicit-omission' as const } : {}) },
      blocks,
    };
  });

  if (resourceBlocks.length && !sections.some(section => section.sectionId === 'appendix')) {
    throw new Error('Frozen site-resource statements require the canonical appendix.');
  }

  if (reportType === 'custom-brief') {
    const dynamicSections = buildCustomBriefQuestionSections(report);
    const conclusionIndex = sections.findIndex((section) => section.sectionId === 'conclusion');
    sections.splice(conclusionIndex < 0 ? sections.length : conclusionIndex, 0, ...dynamicSections);
  }

  if (publicProjection) {
    for (const section of sections) {
      for (const block of section.blocks) {
        block.sourceIds = publicSafeSourceIds(block.sourceIds);
      }
    }
  }

  const qualityDecision = buildQualityDecision(report);
  const reportTitle = REPORT_TYPE_REGISTRY[reportType].label;
  const contentWithoutIdentity = {
    schemaVersion: 'report-document-v1' as const,
    ...(demonstrationDisclosure ? { demonstrationDisclosure } : {}),
    reportId: report.id,
    reportType,
    ...(report.siteId ? { siteId: report.siteId } : {}),
    siteName: report.siteName,
    address: report.address,
    generatedAt: report.createdAt,
    sourceVersion: {
      major: report.version ?? 1,
      minor: report.minorVersion ?? 0,
    },
    title: report.customBriefName || `${report.siteName} — ${reportTitle}`,
    // A withheld `summary` is '' on the body; freeze the notice the private
    // surfaces show there, never an empty cover line. Identity otherwise.
    summary: presented.summary ?? '',
    sections,
    omissions,
    citations: buildCitations(report),
    claims: buildClaims(report, publicProjection),
    evidence: buildEvidence(report, publicProjection),
    assets: [
      ...buildAssets(report, publicProjection),
      ...(publicProjection ? [] : (options.additionalAssets ?? []).map((asset) => ({ ...asset }))),
    ],
    qualityDecision,
  };
  // Storage locators are delivery metadata, not semantic identity. The byte
  // identity of every asset is already carried by `sha256`; excluding `path`
  // lets finalization replace a mutable upload locator with a revision-owned
  // immutable object without changing the document revision (or creating a
  // revision-id/path hash cycle). A changed asset byte hash still changes the
  // document hash and therefore always creates a new revision.
  const contentHash = hashReportDocumentSemanticContent(contentWithoutIdentity);
  const document: ReportDocumentModelV1 = {
    ...contentWithoutIdentity,
    revisionId: `report-revision-${report.id}-${contentHash.slice(0, 16)}`,
    contentHash,
  };
  const parsed = reportDocumentModelV1Schema.parse(document);
  return bindValidatedScrubContext(
    parsed,
    deriveScrubScenarioContext(report as unknown as Record<string, unknown>),
  );
}

export interface BuildPresentationSnapshotV1Options {
  pageSize?: 'A4' | 'Letter';
  density?: PresentationSnapshotV1['density'];
  rendererVersion?: string;
  themeId?: string;
  logoAssetId?: string | null;
  primaryColor?: string;
  accentColor?: string;
  confidentiality?: string | null;
  branding?: PresentationSnapshotV1['branding'];
}

/** Freeze all presentation inputs that may otherwise drift after generation. */
export function buildPresentationSnapshotV1(
  report: SiteRagReport,
  options: BuildPresentationSnapshotV1Options = {},
): PresentationSnapshotV1 {
  const reportType = report.reportType ?? 'feasibility';
  const snapshotWithoutHash = {
    schemaVersion: 'presentation-snapshot-v1' as const,
    templateId: report.templateId ?? null,
    templateVersion: report.templateVersionUsed ?? null,
    themeId: options.themeId ?? 'house-editorial-v1',
    pageSize: options.pageSize ?? 'A4',
    marginsMm: { ...REPORT_PAGE_MARGIN_MM },
    density: options.density ?? 'balanced',
    cover: {
      family: report.coverLayoutAtGen ?? report.coverLayout ?? 'editorial-masthead',
      boundedStandfirst: true,
    },
    fonts: {
      display: 'Fraunces',
      body: 'Newsreader',
      ui: 'Hanken Grotesk',
    },
    branding: options.branding ?? {
      houseMarkId: 'datacrate-wordmark-v1' as const,
      companyName: 'Datacrate',
      siteWordmark: reportText(report.siteName) || 'Unnamed site',
      audienceMode: 'internal' as const,
      logoAssetId: options.logoAssetId ?? null,
      logoAsset: null,
      logoOmission: null,
      primaryColor: options.primaryColor ?? '#123f3b',
      accentColor: options.accentColor ?? '#0f766e',
      confidentiality: options.confidentiality ?? null,
    },
    headingOverrides: report.headingOverrides ?? {},
    sectionOrder: resolveSectionIds(report, reportType),
    sectionColumnLayouts: report.sectionColumnLayouts ?? {},
    rendererVersion: options.rendererVersion ?? 'report-compositor-v1',
  };
  const presentation: PresentationSnapshotV1 = {
    ...snapshotWithoutHash,
    presentationHash: hashCanonicalJson(snapshotWithoutHash),
  };
  return presentationSnapshotV1Schema.parse(presentation);
}
